import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiPortfolioDetail, ApiPosition } from '@/types';
import apiService from '@/services/apiService';
import LCIcon from '@/components/common/LCIcon';
import { fmtCurrency, fmtPct, fmtNumber, fmtPrice, LCBtn, LCChip, LCCushionBar, LCSortHeader, CLS, STATUS_LABEL } from '@/components/common/LCPrimitives';
import EffectiveMarginBreakdown from '@/components/EffectiveMargin/EffectiveMarginBreakdown';
import PortfolioAnalysisTabs from './PortfolioAnalysisTabs';
import { useAuth } from '@/contexts/AuthContext';

type SortField = 'security_name' | 'market_value' | 'weight' | 'pershing_requirement' | 'position_lc_margin_requirement' | 'account_type';
type SortDir = 'asc' | 'desc';

const PortfolioDetail: React.FC = () => {
  const { user } = useAuth();
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [secType, setSecType] = useState('ALL');
  const [sortField, setSortField] = useState<SortField>('weight');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [portfolio, setPortfolio] = useState<ApiPortfolioDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true); setError(null);
    try {
      const p = await apiService.getPortfolioDetail(portfolioId);
      setPortfolio(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch portfolio');
    } finally { setLoading(false); }
  }, [portfolioId]);

  useEffect(() => { fetch(); }, [fetch]);

  const onSort = (f: string) => {
    const field = f as SortField;
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const positions = useMemo(() => {
    if (!portfolio) return [];
    let arr = portfolio.positions.filter(x => {
      if (secType !== 'ALL' && x.security_type !== secType) return false;
      if (search) {
        const q = search.toLowerCase();
        return [x.security_name, x.cusip, x.security_type, x.ticker].some(s => (s || '').toLowerCase().includes(q));
      }
      return true;
    });
    arr.sort((a, b) => {
      const av: any = (a as any)[sortField] ?? 0, bv: any = (b as any)[sortField] ?? 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [portfolio?.positions, search, secType, sortField, sortDir]);

  const breakdown = useMemo(() => {
    if (!portfolio) return [];
    const map: Record<string, { type: string; mv: number; count: number }> = {};
    portfolio.positions.forEach(pos => {
      const k = pos.security_type || 'OTHER';
      if (!map[k]) map[k] = { type: k, mv: 0, count: 0 };
      map[k].mv += pos.market_value || 0;
      map[k].count++;
    });
    const total = Object.values(map).reduce((s, v) => s + v.mv, 0);
    return Object.values(map).map(v => ({ ...v, weight: total > 0 ? v.mv / total : 0 })).sort((a, b) => b.mv - a.mv);
  }, [portfolio?.positions]);

  const secTypes = useMemo(() => Array.from(new Set(portfolio?.positions.map(x => x.security_type).filter(Boolean) as string[])), [portfolio?.positions]);

  if (loading) return (
    <div className={`${CLS.page} flex items-center justify-center min-h-[400px] flex-col gap-4`}>
      <span className={CLS.muted}>Loading portfolio…</span>
    </div>
  );

  if (error || !portfolio) return (
    <div className={CLS.page}>
      <div className="bg-[rgba(229,0,37,0.06)] border border-[#E50025] px-[22px] py-[18px] mb-4">
        <div className="font-bold text-[#E50025] text-[12px] tracking-[0.12em] uppercase mb-1.5">Error</div>
        <div style={{ fontSize: 13 }}>{error || 'Portfolio not found'}</div>
      </div>
      <LCBtn variant="primary" size="sm" onClick={fetch}><LCIcon name="refresh" size={12} /> Retry</LCBtn>
    </div>
  );

  const p = portfolio;

  return (
    <div className={CLS.page}>
      {/* Header */}
      <div className="flex items-end justify-between gap-6 mb-[22px] max-[768px]:flex-col max-[768px]:items-start max-[768px]:gap-3">
        <div>
          <div className="text-[10px] tracking-[0.16em] uppercase text-[#7A7A76] mb-1.5">
            <a href="#" className="text-inherit hover:text-[#E50025] hover:no-underline" onClick={e => { e.preventDefault(); navigate('/'); }}>Dashboard</a>
            <span className="mx-1.5">›</span>
            <span>Portfolio Detail</span>
          </div>
          <h1 className="font-serif text-[36px] font-normal tracking-[-0.015em] leading-tight m-0 max-[480px]:text-[24px]">{p.client_name}</h1>
          <div className="text-[#7A7A76] mt-1.5 text-[12.5px]">
            Portfolio ID: <span className="">{p.portfolio_id_lig}</span>
            {p.rm_name && <>&nbsp;·&nbsp; RM: <span className="">{p.rm_name}</span></>}
            {p.ic_name && <>&nbsp;·&nbsp; IC: <span className="">{p.ic_name}</span></>}
            {p.platform && <>&nbsp;·&nbsp; {p.platform}</>}
          </div>
        </div>
        <div className="flex gap-2.5 max-[768px]:self-end">
          <LCBtn variant="outline" size="sm" onClick={() => navigate('/')}><LCIcon name="back" size={12} /> Back</LCBtn>
          <LCBtn variant="primary" size="sm" onClick={fetch}><LCIcon name="refresh" size={12} /> Refresh</LCBtn>
        </div>
      </div>

      {/* Two-panel summary */}
      <div className="grid grid-cols-2 gap-4 mb-4 max-[768px]:grid-cols-1">
        {/* Margin Status Panel */}
        <div className={`${CLS.surface} ${CLS.surfacePad}`}>
          {p.has_margin_account ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <LCChip status={p.status} label={`Margin Status — ${STATUS_LABEL[p.status?.toUpperCase().replace(/ /g, '_') || ''] || p.status || '—'}`} />
                {p.is_margin_trading && <LCChip variant="solid-warn" label="Active Margin Trading" />}
              </div>
              <div className="mb-3.5">
                <div className={CLS.eyebrow}>Surplus Cushion</div>
                <div className="mt-2"><LCCushionBar value={p.surplus_cushion} status={p.status} big /></div>
              </div>
              <div className="mb-[18px]">
                <div className={CLS.eyebrow}>With Cash &amp; Equivalents</div>
                <div className="mt-2"><LCCushionBar value={p.surplus_cushion_with_cash} status={p.status} big /></div>
              </div>
              <div className="grid grid-cols-2 gap-[18px_28px]">
                {[
                  ['Total AUM', fmtCurrency(p.total_aum)],
                  ['Margin Equity', fmtCurrency(p.margin_account_equity)],
                  ['Pershing Req.', fmtCurrency(p.pershing_requirement)],
                  ['LC Margin Req.', fmtCurrency(p.lc_margin_requirement)],
                  ['Available Margin', fmtCurrency((p.total_aum || 0) - (p.lc_margin_requirement || 0))],
                  ['Cash & Equiv.', fmtCurrency(p.cash_and_cash_equivalent)],
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">{lbl}</div>
                    <div className="font-serif text-[22px] font-normal mt-1 tracking-[-0.005em]">{val}</div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <LCChip variant="success" label="Cash-Only Account" />
              <div className="grid grid-cols-2 gap-[18px_28px] mt-[18px]">
                {[
                  ['Total AUM', fmtCurrency(p.total_aum)],
                  ['Cash Equity', fmtCurrency(p.cash_account_equity)],
                  ['Cash & Equiv.', fmtCurrency(p.cash_and_cash_equivalent)],
                  ['Positions', String(p.positions.length)],
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">{lbl}</div>
                    <div className="font-serif text-[22px] font-normal mt-1 tracking-[-0.005em]">{val}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Team & Breakdown Panel */}
        <div className={`${CLS.surface} ${CLS.surfacePad}`}>
          <div className={`${CLS.eyebrow} text-[#E50025]`}>Team &amp; Account</div>
          <div className="grid grid-cols-2 gap-[18px_28px] mt-2">
            {[
              ['Relationship Manager', p.rm_name || '—'],
              ['Investment Counselor', p.ic_name || '—'],
              ['Platform', p.platform || '—'],
              ['Risk Score', p.risk_score ? p.risk_score.charAt(0).toUpperCase() + p.risk_score.slice(1).toLowerCase() : '—'],
              ['Positions', String(p.positions.length)],
              ['Last Updated', p.last_updated ? new Date(p.last_updated).toLocaleString() : '—'],
            ].map(([lbl, val]) => (
              <div key={lbl}>
                <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#7A7A76]">{lbl}</div>
                <div className="text-[22px] font-serif font-normal mt-1 tracking-[-0.005em]">{val}</div>
              </div>
            ))}
          </div>

          <hr className={CLS.hr} />
          <div className={CLS.eyebrow}>Breakdown by Asset Class</div>
          <div className="mt-2.5">
            {breakdown.map(b => (
              <div key={b.type} className="grid items-center py-2 border-b border-black/[0.05]" style={{ gridTemplateColumns: '120px 1fr 80px 100px' }}>
                <span className="text-[11px] font-bold tracking-[0.06em] uppercase">{b.type}</span>
                <div className="h-1.5 bg-black/[0.06] relative">
                  <div className="absolute inset-0 bg-[#E50025]" style={{ width: `${b.weight * 100}%` }} />
                </div>
                <span className="text-right text-[12px] text-[#7A7A76]">{(b.weight * 100).toFixed(1)}%</span>
                <span className="text-right text-[12px] font-semibold">{fmtCurrency(b.mv)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PortfolioAnalysisTabs
        canViewEffectiveMargin={user?.can_view_effective_margin === true}
        filteredPositionCount={positions.length}
        totalPositionCount={p.positions.length}
        positions={<>
      <div className={`${CLS.surface} p-[14px_16px] mb-3.5 grid [grid-template-columns:2fr_1fr] gap-3`}>
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Search</label>
          <div className={CLS.inputWrap}>
            <LCIcon name="search" size={14} className={CLS.inputIco} />
            <input className={`${CLS.input} pl-[38px]`} placeholder="Security name, CUSIP, ticker…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div className={CLS.field}>
          <label className={CLS.fieldLabel}>Security Type</label>
          <select className={CLS.input} value={secType} onChange={e => setSecType(e.target.value)}>
            <option value="ALL">All types</option>
            {secTypes.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className={CLS.tableWrap}>
        <table className={CLS.table}>
          <thead>
            <tr>
              <th className={CLS.th} style={{ width: 110 }}>CUSIP</th>
              <LCSortHeader label="Security" field="security_name" active={sortField === 'security_name'} dir={sortDir} onClick={onSort} />
              <th className={CLS.th}>Type</th>
              <LCSortHeader label="Account" field="account_type" active={sortField === 'account_type'} dir={sortDir} onClick={onSort} />
              <th className={`${CLS.th} ${CLS.tdNum}`}>Quantity</th>
              <th className={`${CLS.th} ${CLS.tdNum}`}>Price</th>
              <LCSortHeader label="Market Value" field="market_value" active={sortField === 'market_value'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
              <LCSortHeader label="Weight" field="weight" active={sortField === 'weight'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
              <LCSortHeader label="Pershing Req." field="pershing_requirement" active={sortField === 'pershing_requirement'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
              <th className={`${CLS.th} ${CLS.tdNum}`}>LC Add-on</th>
              <LCSortHeader label="LC Req." field="position_lc_margin_requirement" active={sortField === 'position_lc_margin_requirement'} dir={sortDir} onClick={onSort} className={CLS.tdNum} />
            </tr>
          </thead>
          <tbody>
            {positions.map((x, i) => (
              <tr key={(x.cusip || '') + i} className="transition-colors hover:bg-[#FAF9F8]">
                <td className={`${CLS.td} ${CLS.tdMono}`}>{x.cusip || '—'}</td>
                <td className={CLS.td}>
                  <div className="font-medium">{x.security_name || '—'}</div>
                  {x.ticker && <div className={`${CLS.tdMono} text-[10px] ${CLS.muted}`}>{x.ticker}</div>}
                </td>
                <td className={CLS.td}><LCChip variant="outline" label={x.security_type || '—'} /></td>
                <td className={CLS.td}>{x.account_type || '—'}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtNumber(x.quantity)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtPrice(x.market_price)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(x.market_value)}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{x.weight != null ? `${(x.weight * 100).toFixed(2)}%` : '—'}</td>
                <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(x.pershing_requirement)}</td>
                <td className={`${CLS.td} ${CLS.tdNum} text-[#E50025]`}>{x.lc_addon != null ? `${(x.lc_addon * 100).toFixed(1)}%` : '—'}</td>
                <td className={`${CLS.td} ${CLS.tdNum} font-semibold text-[#E50025]`}>{fmtCurrency(x.position_lc_margin_requirement)}</td>
              </tr>
            ))}
            {positions.length === 0 && (
              <tr><td colSpan={11} className={`${CLS.td} ${CLS.muted}`} style={{ padding: 30, textAlign: 'center' }}>No positions match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
        </>}
        effective={showPositions => (
          <EffectiveMarginBreakdown portfolioId={p.portfolio_id_lig} onViewPositions={showPositions} />
        )}
      />
    </div>
  );
};

export default PortfolioDetail;
